import React, { useMemo, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDeals, useDealsPipeline, useDealMutations } from '../hooks/useCRM';
import DealPipeline from '../components/DealPipeline';
import DealForm from '../components/DealForm';
import { useToast } from '../components/Toast';
import { useConfirm } from '../hooks/useConfirm';
import '../crm.css';
import { dealPath } from '../utils/crmEnv';

const stageOptions = [
    { value: '', label: 'All Stages' },
    { value: 'new', label: 'New Inquiry' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'proposal', label: 'Proposal' },
    { value: 'site_visit', label: 'Site Visit' },
    { value: 'negotiation', label: 'Negotiation / Booking' },
    { value: 'won', label: 'Closed Won' },
    { value: 'lost', label: 'Closed Lost' }
];

const DealsPage = () => {
    const history = useHistory();
    const [viewMode, setViewMode] = useState('pipeline');
    // Modals
    const [showModal, setShowModal] = useState(false);
    const [editingDeal, setEditingDeal] = useState(null);
    const [showOutcomeModal, setShowOutcomeModal] = useState(false);
    const [stageMovePending, setStageMovePending] = useState(null);

    // Outcome Data (for Won/Lost)
    const [outcomeData, setOutcomeData] = useState({
        dealId: null,
        stage: '',
        lostReason: ''
    });

    // Filters
    const [filters, setFilters] = useState({
        search: '',
        leadQ: '',
        leadIntent: '',
        stage: '',
        propertyType: '',
    });
    const [searchInput, setSearchInput] = useState('');
    const [leadSearchInput, setLeadSearchInput] = useState('');

    // Hooks
    const listParams = {
        q: filters.search,
        leadQ: filters.leadQ,
        leadIntent: filters.leadIntent,
        propertyType: filters.propertyType,
        stage: filters.stage,
        page: 1,
        pageSize: 25,
    };
    const pipelineParams = {
        q: filters.search,
        leadQ: filters.leadQ,
        leadIntent: filters.leadIntent,
        propertyType: filters.propertyType,
        stage: filters.stage,
    };
    const { pipeline, loading: pipelineLoading, refetch: refetchPipeline } = useDealsPipeline(pipelineParams);
    const { deals, loading: dealsLoading, refetch: refetchDeals } = useDeals(listParams);
    const { deals: allDeals, loading: allDealsLoading, refetch: refetchAllDeals } = useDeals({
        ...listParams,
        includeArchived: true,
        page: 1,
        pageSize: 200,
    });
    const { create, update, updateStage, remove, loading: mutationLoading } = useDealMutations();
    const { showSuccess, showError } = useToast();
    const { confirm, ConfirmDialog } = useConfirm();

    // Debounce Search
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setFilters(prev => ({ ...prev, search: searchInput }));
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [searchInput]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setFilters(prev => ({ ...prev, leadQ: leadSearchInput }));
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [leadSearchInput]);


    // Handlers
    const handleDealClick = (deal) => {
        if (!deal?.id) return;
        history.push(dealPath(deal.id));
    };

    const handleStageChange = async (dealId, newStage) => {
        // Intercept Won/Lost for outcome modal
        if (newStage === 'won' || newStage === 'lost') {
            setOutcomeData({ dealId, stage: newStage, lostReason: '' });
            setShowOutcomeModal(true);
            return;
        }

        try {
            setStageMovePending({ dealId, targetStage: newStage });
            await updateStage(dealId, newStage);
            await Promise.all([refetchPipeline(), refetchDeals(), refetchAllDeals()]);
        } catch (err) {
            console.error('Failed to update stage:', err);
            showError('Failed to update stage');
        } finally {
            setStageMovePending(null);
        }
    };

    const handleOutcomeSubmit = async () => {
        try {
            await update(outcomeData.dealId, {
                stage: outcomeData.stage,
                lostReason: outcomeData.stage === 'lost' ? outcomeData.lostReason : null,
                actualCloseDate: new Date().toISOString()
            });
            setShowOutcomeModal(false);
            setOutcomeData({ dealId: null, stage: '', lostReason: '' });
            refetchPipeline();
            refetchDeals();
            refetchAllDeals();
            showSuccess(`Deal marked as ${outcomeData.stage}`);
        } catch (err) {
            console.error('Failed to update deal outcome:', err);
            showError('Failed to update deal outcome');
        }
    };

    const handleSubmit = async (formData) => {
        try {
            if (editingDeal) {
                await update(editingDeal.id, formData);
                showSuccess('Deal updated successfully');
            } else {
                await create(formData);
                showSuccess('Deal created successfully');
            }
            setShowModal(false);
            setEditingDeal(null);
            refetchPipeline();
            refetchDeals();
            refetchAllDeals();
        } catch (err) {
            console.error('Failed to save deal:', err);
            showError('Failed to save deal');
        }
    };

    const handleDelete = async (id) => {
        const isConfirmed = await confirm({
            title: 'Delete Deal',
            message: 'Are you sure you want to delete this deal? This cannot be undone.',
            confirmText: 'Delete',
            variant: 'danger'
        });

        if (isConfirmed) {
            try {
                await remove(id);
                showSuccess('Deal deleted successfully');
                refetchPipeline();
                refetchDeals();
                refetchAllDeals();
            } catch (err) {
                console.error('Failed to delete deal:', err);
                showError('Failed to delete deal');
            }
        }
    };

    const handleEdit = (deal) => {
        // Prevent editing if click originated from drag (handled in pipeline but good safety)
        setEditingDeal(deal);
        setShowModal(true);
    };

    // Formatters
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(value || 0);
    };

    const loading = viewMode === 'pipeline'
        ? pipelineLoading
        : dealsLoading;

    const pipelineMap = useMemo(() => {
        if (!pipeline?.stages) return null;
        const map = {};
        const metrics = {};
        pipeline.stages.forEach((stage) => {
            map[stage.stageId] = {
                deals: stage.items || [],
                count: stage.count,
                value: stage.totalAmount,
            };
            metrics[stage.stageId] = {
                count: stage.count,
                value: stage.totalAmount,
                overdueCount: stage.overdueCount || 0,
            };
        });
        return { map, metrics };
    }, [pipeline]);

    const archivedDeals = useMemo(() => {
        return (allDeals || []).filter((deal) => deal.archived);
    }, [allDeals]);

    return (
        <div className="crm-container">
            {/* Header */}
            <div className="crm-page-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    <h1 className="crm-page-title" style={{ margin: 0 }}>Deals</h1>
                    <div className="crm-view-toggle" style={{ display: 'flex', gap: '8px' }}>
                        <button
                            className={`crm-btn ${viewMode === 'pipeline' ? 'crm-btn-primary' : 'crm-btn-secondary'}`}
                            onClick={() => setViewMode('pipeline')}
                            style={{ padding: '6px 12px', fontSize: '13px' }}
                        >
                            Pipeline view
                        </button>
                        <button
                            className={`crm-btn ${viewMode === 'table' ? 'crm-btn-primary' : 'crm-btn-secondary'}`}
                            onClick={() => setViewMode('table')}
                            style={{ padding: '6px 12px', fontSize: '13px' }}
                        >
                            Table view
                        </button>
                    </div>
                </div>
                <button
                    className="crm-btn crm-btn-primary"
                    onClick={() => { setEditingDeal(null); setShowModal(true); }}
                    style={{ whiteSpace: 'nowrap' }}
                >
                    + Add Deal
                </button>
            </div>

            {/* Filters */}
            <div className="crm-filters" style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
                <input
                    type="text"
                    className="crm-input crm-search-input"
                    placeholder="Search deals..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    style={{ maxWidth: '300px' }}
                />
                <input
                    type="text"
                    className="crm-input"
                    placeholder="Lead name or ID"
                    value={leadSearchInput}
                    onChange={(e) => setLeadSearchInput(e.target.value)}
                    style={{ maxWidth: '240px' }}
                />

                <select
                    className="crm-select"
                    value={filters.propertyType}
                    onChange={(e) => setFilters(prev => ({ ...prev, propertyType: e.target.value }))}
                    style={{ maxWidth: '200px' }}
                >
                    <option value="">All Property Types</option>
                    <option value="plot">Plot</option>
                    <option value="villa">Villa</option>
                    <option value="apartment">Apartment</option>
                    <option value="commercial">Commercial</option>
                </select>

                <select
                    className="crm-select"
                    value={filters.leadIntent}
                    onChange={(e) => setFilters(prev => ({ ...prev, leadIntent: e.target.value }))}
                    style={{ maxWidth: '200px' }}
                >
                    <option value="">All Lead Intents</option>
                    <option value="buy">Buy</option>
                    <option value="rent">Rent</option>
                    <option value="sell">Sell</option>
                    <option value="invest">Invest</option>
                    <option value="other">Other</option>
                </select>

                {viewMode === 'table' && (
                    <select
                        className="crm-select"
                        value={filters.stage}
                        onChange={(e) => setFilters(prev => ({ ...prev, stage: e.target.value }))}
                        style={{ maxWidth: '200px' }}
                    >
                        {stageOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                )}

            </div>

            {/* Content */}

            {loading ? (
                <div className="crm-loading">
                    <div className="crm-spinner"></div>
                </div>
            ) : viewMode === 'pipeline' ? (
                <DealPipeline
                    pipeline={pipelineMap?.map || null}
                    onStageChange={handleStageChange}
                    onDealClick={handleDealClick}
                    stageMetrics={pipelineMap?.metrics}
                    movingDealId={stageMovePending?.dealId || null}
                    isStageUpdating={!!stageMovePending}
                    movingTargetStage={stageMovePending?.targetStage || null}
                />
            ) : (
                <div className="crm-card">
                    <div className="crm-card-body" style={{ padding: 0 }}>
                        <table className="crm-table">
                            <thead>
                                <tr>
                                    <th>Title</th>
                                    <th>Contact</th>
                                    <th>Value</th>
                                    <th>Stage</th>
                                    <th>Expected Close</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(deals || []).length === 0 ? (
                                    <tr>
                                        <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: 'var(--crm-gray-600)' }}>
                                            No deals found.
                                        </td>
                                    </tr>
                                ) : (
                                    (deals || []).map(deal => (
                                        <tr key={deal.id} onClick={() => handleDealClick(deal)} style={{ cursor: 'pointer' }}>
                                            <td style={{ fontWeight: '500' }}>{deal.title}</td>
                                            <td>{deal.contactName || '-'}</td>
                                            <td>{formatCurrency(deal.value)}</td>
                                            <td>
                                                <span className={`crm-badge crm-badge-${deal.stage}`}>
                                                    {stageOptions.find(o => o.value === deal.stage)?.label || deal.stage}
                                                </span>
                                            </td>
                                            <td>
                                                {deal.expectedCloseDate ? new Date(deal.expectedCloseDate).toLocaleDateString() : '-'}
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', gap: '8px' }} onClick={e => e.stopPropagation()}>
                                                    <button
                                                        className="crm-btn crm-btn-secondary crm-btn-sm"
                                                        onClick={() => handleEdit(deal)}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        className="crm-btn crm-btn-danger crm-btn-sm"
                                                        onClick={() => handleDelete(deal.id)}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Past Deals */}
            {!allDealsLoading && archivedDeals.length > 0 && (
                <div className="crm-card" style={{ marginTop: '24px' }}>
                    <div className="crm-card-header">
                        <h2 className="crm-card-title">Past Deals</h2>
                    </div>
                    <div className="crm-card-body">
                        {archivedDeals.map((deal) => (
                            <div key={deal.id} className="crm-card crm-card-hover" style={{ marginBottom: '12px' }}>
                                <div className="crm-card-body">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                                        <div>
                                            <h3 style={{ margin: 0 }}>{deal.title || deal.name || 'Untitled Deal'}</h3>
                                            <div className="crm-help-text">
                                                Archived {deal.archivedAt ? new Date(deal.archivedAt).toLocaleString() : 'recently'}
                                            </div>
                                        </div>
                                        <div>
                                            <span className="crm-badge crm-badge-muted">Archived</span>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', gap: '16px', marginTop: '12px', flexWrap: 'wrap' }}>
                                        <div>
                                            <div className="crm-field-label">Value</div>
                                            <div className="crm-field-value">{formatCurrency(deal.value)}</div>
                                        </div>
                                        <div>
                                            <div className="crm-field-label">Expected close</div>
                                            <div className="crm-field-value">
                                                {deal.expectedCloseDate ? new Date(deal.expectedCloseDate).toLocaleDateString() : '-'}
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                        <button className="crm-btn crm-btn-secondary crm-btn-sm" onClick={() => handleDealClick(deal)}>
                                            Open Deal
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="crm-modal-overlay" onClick={() => setShowModal(false)}>
                    <div
                        className="crm-modal"
                        onClick={e => e.stopPropagation()}
                        style={{ maxWidth: '600px', width: '100%', maxHeight: '90vh', overflow: 'auto', background: 'var(--crm-bg)' }}
                    >
                        <div className="crm-modal-header">
                            <h2 className="crm-modal-title">{editingDeal ? 'Edit Deal' : 'Add Deal'}</h2>
                            <button className="crm-modal-close" onClick={() => setShowModal(false)}>×</button>
                        </div>
                        <div className="crm-modal-body">
                            <DealForm
                                deal={editingDeal}
                                onSubmit={handleSubmit}
                                onCancel={() => setShowModal(false)}
                                loading={mutationLoading}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Outcome Modal (Won/Lost) */}
            {showOutcomeModal && (
                <div className="crm-modal-overlay" onClick={() => setShowOutcomeModal(false)}>
                    <div className="crm-modal" onClick={e => e.stopPropagation()} style={{ background: 'var(--crm-bg)' }}>
                        <div className="crm-modal-header">
                            <h2 className="crm-modal-title">
                                {outcomeData.stage === 'won' ? '🎉 Mark Deal as Won' : 'Mark Deal as Lost'}
                            </h2>
                            <button className="crm-modal-close" onClick={() => setShowOutcomeModal(false)}>×</button>
                        </div>
                        <div className="crm-modal-body">
                            {outcomeData.stage === 'won' ? (
                                <p>Are you sure you want to mark this deal as Won? This will move it to the closed stage.</p>
                            ) : (
                                <div className="crm-form-group">
                                    <label className="crm-label required">Reason for Loss</label>
                                    <textarea
                                        className="crm-textarea"
                                        value={outcomeData.lostReason}
                                        onChange={(e) => setOutcomeData({ ...outcomeData, lostReason: e.target.value })}
                                        rows={3}
                                        placeholder="Price too high, competitor won, etc."
                                    />
                                </div>
                            )}

                            <div className="crm-form-actions">
                                <button className="crm-btn crm-btn-secondary" onClick={() => setShowOutcomeModal(false)}>
                                    Cancel
                                </button>
                                <button
                                    className={`crm-btn ${outcomeData.stage === 'won' ? 'crm-btn-success' : 'crm-btn-danger'}`}
                                    onClick={handleOutcomeSubmit}
                                    disabled={outcomeData.stage === 'lost' && !outcomeData.lostReason}
                                >
                                    Confirm {outcomeData.stage === 'won' ? 'Won' : 'Lost'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <ConfirmDialog />
        </div>
    );
};

export default DealsPage;
